import { useEffect } from 'react';
import { RecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import {
    crossSectionApplyRequestState,
    crossSectionPreviewReadyState,
    crossSectionShapeRequestState,
} from '~/state/atoms/toolsAtoms';
import { useCrossSectionPanelAutoOpen } from '~/hooks/viewer/useCrossSectionPanelAutoOpen';
import { useCrossSectionPreview } from './useCrossSectionPreview';

/** The highest value of each request counter already handed to a pass. */
const watermarks = new Map<RecoilState<number>, number>();

/** Run `handle` once per bump of a monotonic request counter, including bumps made while unmounted. */
function useRequest(
    request: RecoilState<number>,
    handle: () => Promise<void>
): void {
    const value = useRecoilValue(request);
    useEffect(() => {
        if (value !== (watermarks.get(request) ?? 0)) {
            watermarks.set(request, value);
            void handle();
        }
    }, [request, value, handle]);
}

/** Headless owner of the Cross-Section preview and its Apply, mounted for the tool selection. */
export default function CrossSectionPreviewHost() {
    const resetShapeRequest = useResetRecoilState(
        crossSectionShapeRequestState
    );
    const resetApplyRequest = useResetRecoilState(
        crossSectionApplyRequestState
    );
    const resetPreviewReady = useResetRecoilState(
        crossSectionPreviewReadyState
    );
    const { recompute, apply } = useCrossSectionPreview();
    useCrossSectionPanelAutoOpen();
    useRequest(crossSectionShapeRequestState, recompute);
    useRequest(crossSectionApplyRequestState, apply);

    // Counters and watermarks are reset as a pair, so no request leaks into or is lost from the next session.
    useEffect(
        () => () => {
            watermarks.clear();
            resetShapeRequest();
            resetApplyRequest();
            resetPreviewReady();
        },
        [resetShapeRequest, resetApplyRequest, resetPreviewReady]
    );

    return null;
}
